/**
 * "Already a member" detection for /plan/subscribe (decision 2026-10-24: trial AND paid members,
 * on either billing flow, never see a second purchase; it would bill them twice).
 *
 * Data: user-getcurrentstreamingplanbyuserid -> {activePlan: {userLiveStreamingPlans: [plan docs]}}.
 * `status` is ACTIVE on every signup doc, so it is ignored. A member has ANY plan doc whose
 * `active_plan_name` is set ('FREE TRIAL', '3 MONTHS', ...) and whose `endDate` is in the future.
 * Users can have several plan docs, so all are checked (never just [0]).
 */

export interface ActiveMembership {
	planName: string;
	/** Epoch ms; null when only the backend's 409 told us (no plan details). */
	endsAt: number | null;
}

/** Thrown when the backend refuses a purchase with 409 {error: 'mandate_exists'}. */
export class MembershipExistsError extends Error {
	constructor() {
		super('mandate_exists');
		this.name = 'MembershipExistsError';
	}
}

/** Firestore Timestamp as JSON ({_seconds} / {seconds}), ISO string or epoch ms -> epoch ms. */
export const timestampToMs = (value: any): number | null => {
	if (value == null) return null;
	const seconds = value?._seconds ?? value?.seconds;
	if (typeof seconds === 'number') return seconds * 1000;
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const ms = Date.parse(value);
		return Number.isNaN(ms) ? null : ms;
	}
	return null;
};

export const planDocsFromResponse = (response: any): any[] => {
	const docs = response?.activePlan?.userLiveStreamingPlans;
	return Array.isArray(docs) ? docs : [];
};

/** The membership that runs longest, or null. */
export const findActiveMembership = (
	planDocs: any[],
	now: number
): ActiveMembership | null => {
	let best: ActiveMembership | null = null;
	for (const doc of planDocs || []) {
		const name =
			typeof doc?.active_plan_name === 'string' ? doc.active_plan_name.trim() : '';
		const endsAt = timestampToMs(doc?.endDate);
		if (!name || endsAt === null || endsAt <= now) continue;
		if (!best || (best.endsAt ?? 0) < endsAt) best = { planName: name, endsAt };
	}
	return best;
};

/** 409 {error: 'mandate_exists'} from createwebmandate or the legacy enrol endpoint. */
export const isMandateExistsResponse = (response: any): boolean => {
	if (response?.status !== 409) return false;
	const data = response?.data;
	if (data && typeof data === 'object') return data.error === 'mandate_exists';
	return typeof data === 'string' && data.includes('mandate_exists');
};

/** One line for the member card. */
export const describeMembership = (
	membership: ActiveMembership,
	formatDate: (ms: number) => string
): string => {
	if (membership.endsAt === null) return 'Your membership is already active.';
	const until = formatDate(membership.endsAt);
	if (/trial/i.test(membership.planName)) return `Your free trial is active until ${until}.`;
	return `Your ${membership.planName.toLowerCase()} membership is active until ${until}.`;
};
